import { Button, Card, CardContent, Input, Label } from "@exhousing/design-system";
import { CalendarPlus, Loader2, X } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { useViewingInvites } from "@/hooks/api/useApplications";

/**
 * Bulk viewing invites (journey 4.2): pick a score threshold, preview who gets what,
 * send. Candidates ≥ threshold are invited; unsubmitted ones get a completion nudge.
 */
export function InviteModal({
  propertyId,
  onClose,
}: {
  propertyId: string;
  onClose: () => void;
}) {
  const invites = useViewingInvites();
  const [minScore, setMinScore] = useState("70");
  const [preview, setPreview] = useState<{
    invited: number;
    reminded: number;
    skipped: number;
  } | null>(null);

  const score = Math.min(100, Math.max(0, Number(minScore) || 0));

  useEffect(() => {
    const handle = setTimeout(() => {
      invites.mutate(
        { propertyId, minScore: score, dryRun: true },
        { onSuccess: (counts) => setPreview(counts), onError: () => setPreview(null) },
      );
    }, 250);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId, score]);

  const send = () =>
    invites.mutate(
      { propertyId, minScore: score, dryRun: false },
      {
        onSuccess: (counts) => {
          toast.success(
            `${counts.invited} invitation(s) sent, ${counts.reminded} reminder(s)`,
          );
          onClose();
        },
        onError: (err) => toast.error(err.message),
      },
    );

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/40 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <Card className="w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <CardContent className="space-y-4 pt-6">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="font-display text-xl font-medium">Viewing invitations</h2>
              <p className="mt-0.5 text-sm text-muted-foreground">
                Invite scored candidates; nudge unfinished applications.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="rounded-lg p-1 text-muted-foreground hover:bg-muted"
            >
              <X className="size-5" />
            </button>
          </div>

          <div className="space-y-2">
            <Label htmlFor="min-score">Minimum score</Label>
            <Input
              id="min-score"
              type="number"
              min={0}
              max={100}
              value={minScore}
              onChange={(e) => setMinScore(e.target.value)}
            />
          </div>

          <div className="rounded-lg border border-border bg-muted/40 p-4 text-sm">
            {preview ? (
              <ul className="space-y-1">
                <li>
                  <strong>{preview.invited}</strong> candidate(s) with score ≥ {score}{" "}
                  get a viewing invitation.
                </li>
                <li>
                  <strong>{preview.reminded}</strong> unfinished application(s) get a
                  completion reminder.
                </li>
                {preview.skipped > 0 && (
                  <li className="text-muted-foreground">
                    {preview.skipped} below the threshold stay untouched.
                  </li>
                )}
              </ul>
            ) : (
              <span className="inline-flex items-center gap-2 text-muted-foreground">
                <Loader2 className="size-4 animate-spin" /> Calculating…
              </span>
            )}
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              disabled={invites.isPending || !preview || preview.invited === 0}
              onClick={send}
            >
              <CalendarPlus className="size-4" />
              Send {preview?.invited ?? 0} invitation(s)
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
